import { PkDialog, PkSpinner } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useEventbus } from '@hooks/useEventbus';
import { useWindowSize } from '@hooks/useWindowSize';
import { withErrorBoundary } from '@shared/ErrorBoundary';
import ReactPortal from '@shared/ReactPortal';
import { useTranslateFn } from '@shared/Translate';
import type { Fan } from '@shared-types/idp.types';
import { AUTH_FLOW_TYPE, UserEvents } from '@shared-types/idp.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import IdpHandlerInstance from '@user/IdpHandler';

import * as styles from './styles.module.scss';

const AuthDialog = () => {
  const t = useTranslateFn();
  const { isDesktop } = useWindowSize();

  const [isOpen, setIsOpen] = useState(false);
  const wasOpen = useRef(false);
  const [flowType, setFlowType] = useState<AUTH_FLOW_TYPE>(AUTH_FLOW_TYPE.REGISTRATION);

  useEventbus((type: AUTH_FLOW_TYPE) => {
    setIsOpen(true);
    wasOpen.current = true;
    setFlowType(type);
  }, 'openAuthDialog');

  const onLogin = ({ user }: { user?: Fan }) => {
    if (!user?.UID) return;

    wasOpen.current = false;
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    IdpHandlerInstance.waitIdpReady().then((idp) => {
      const eventEmitter = idp.getEventEmitter();

      eventEmitter.once(UserEvents.LOGIN, onLogin);
    });
  }, [isOpen, flowType]);

  const trackClose = useCallback(() => {
    const action = flowType === AUTH_FLOW_TYPE.LOGIN ? 'login' : 'register';
    const pathname = window.location.pathname.endsWith('/')
      ? window.location.pathname.slice(0, -1)
      : window.location.pathname;
    GoogleTagManagerHelper.ga4Event('gigya_close', { interaction: action, section_reference: pathname });
  }, [flowType]);

  const handleClose = () => {
    setIsOpen(false);

    if (!wasOpen.current) return;
    wasOpen.current = false;

    trackClose();
  };

  return (
    <ReactPortal>
      <PkDialog
        isOpen={isOpen}
        onPkClose={handleClose}
        size={isDesktop ? 'large' : 'full'}
        pk-theme='light'
        className={clsx(styles.dialog, 'pk-p--0')}
      >
        <div slot='body' className={clsx('pk-w--100', styles.panelContent)}>
          <div
            id='host-site-screens-wrapper-container'
            className={clsx(
              styles.myuefaCustomAuth,
              'pk-px--m pk-py--l pk-p-lg--xl2 pk-d--flex pk-flex--column pk-justify-content--between',
            )}
          >
            <div className={styles.myuefaLogo} />

            <div>
              <p className='pk-font-size--xl pk-font-size-lg--xl2 pk-font--base-bold'>{t('myuefa_login_headline')}</p>
              <ul>
                <li>{t('myuefa_login_benefit1')}</li>
                <li>{t('myuefa_login_benefit2')}</li>
                <li>{t('myuefa_login_benefit3')}</li>
                <li>{t('myuefa_login_benefit4')}</li>
              </ul>
            </div>

            {/* desktop left side footer */}
            <div className='pk-d--none pk-d-md--block'>
              <p className='pk-font-size--s'>{t('myuefa_promo_custom_access')}</p>
              <div className='pk-d--flex pk-gap--s'>
                <div className={clsx(styles.icon, styles.ucl)} />
                <div className={clsx(styles.icon, styles.weuro2025)} />
                <div className={clsx(styles.icon, styles.uefatv)} />
                <div className={clsx(styles.icon, styles.uefagaming)} />
                <span className={clsx(styles.iconsSuffix, 'pk-align-self--end pk-font-size--xs')}>
                  {t('and_much_more')}
                </span>
              </div>
            </div>
          </div>

          {isOpen && (
            <div id='idp-screens-wrapper-container' className={clsx(styles.idpWrapper, 'pk-text--center')}>
              <PkSpinner className='pk-d--flex pk-h--100' />
            </div>
          )}

          {/* mobile footer */}
          <div className='pk-w--100 pk-px--m pk-py--l pk-d--block pk-d-md--none'>
            <div className='pk-text--center'>
              <p className='pk-font-size--s pk-mb--m'>{t('myuefa_promo_custom_access')}</p>
              <div className='pk-d--flex pk-justify-content--center pk-gap--s'>
                <div className={clsx(styles.icon, styles.ucl)} />
                <div className={clsx(styles.icon, styles.euro2024)} />
                <div className={clsx(styles.icon, styles.uefatv)} />
                <div className={clsx(styles.icon, styles.uefagaming)} />
              </div>
              <div className='pk-font-size--xs pk-mt--xs'>{t('and_much_more')}</div>
            </div>
          </div>
        </div>
      </PkDialog>
    </ReactPortal>
  );
};

export default withErrorBoundary(memo(AuthDialog));
